import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BookOpenIcon,
  UserCircleIcon,
  ChevronDownIcon,
  LinkIcon,
  UsersIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';

// --- START: MOCK DATA for a single course ---
const mockCourse = {
  id: 1,
  title: 'Introduction to Agile Project Management',
  category: 'Project Management',
  instructor: {
    name: 'Sanjay Kumar',
    bio: 'Sanjay has over 15 years of experience leading software teams using Agile and Scrum methodologies. He is a certified Scrum Master and has trained hundreds of professionals.',
  },
  description: 'This foundational course introduces you to the principles of Agile project management. You will learn to apply the Scrum framework, conduct sprint planning, and effectively manage project backlogs to deliver value incrementally.',
  modules: [
    { title: 'Module 1: Agile Fundamentals', lessons: ['What is Agile?', 'The Agile Manifesto', 'Agile Principles in Practice'] },
    { title: 'Module 2: The Scrum Framework', lessons: ['Scrum Roles', 'Scrum Events: Sprint Planning, Daily Scrum, Review & Retrospective', 'Scrum Artifacts: Product Backlog, Sprint Backlog'] },
    { title: 'Module 3: Managing the Project Lifecycle', lessons: ['Creating User Stories', 'Estimation Techniques', 'Prioritizing the Backlog'] },
  ],
  resources: [
    { title: 'Agile & Scrum Glossary', link: '#' },
    { title: 'Sample User Story Template', link: '#' },
    { title: 'Video: The Power of Retrospective Meetings', link: '#' },
  ],
  sessions: [
    { date: '2025-10-01', time: '10:00 AM' },
    { date: '2025-10-08', time: '10:00 AM' },
  ],
};
// --- END: MOCK DATA ---

const CourseDetails = () => {
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [expandedModule, setExpandedModule] = useState(null);

  const handleEnroll = () => {
    setIsEnrolled(true);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-sky-200 p-8 text-gray-800">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <h1 className="text-4xl font-extrabold text-indigo-800">{mockCourse.title}</h1>
        <p className="text-xl font-light text-indigo-600 mt-2">{mockCourse.category}</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {/* Main Content */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="lg:col-span-2 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <div className="border-b pb-4 mb-4">
            <h3 className="text-2xl font-bold text-gray-900">Course Overview</h3>
            <p className="text-gray-600 mt-2">{mockCourse.description}</p>
          </div>

          <div className="border-b pb-4 mb-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Course Curriculum</h3>
            <div className="space-y-4">
              {mockCourse.modules.map((module, index) => (
                <div key={index} className="border border-gray-200 rounded-xl">
                  <button
                    type="button"
                    className="flex justify-between items-center w-full p-4 text-left font-medium text-gray-900"
                    onClick={() => setExpandedModule(expandedModule === index ? null : index)}
                  >
                    <span>{module.title}</span>
                    <ChevronDownIcon className={`h-5 w-5 transform transition-transform ${expandedModule === index ? 'rotate-180' : 'rotate-0'}`} />
                  </button>
                  <AnimatePresence>
                    {expandedModule === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ul className="px-4 pb-4 space-y-2 text-sm text-gray-600 border-t border-gray-200 pt-4">
                          {module.lessons.map((lesson, lessonIndex) => (
                            <li key={lessonIndex} className="flex items-center space-x-2">
                              <BookOpenIcon className="h-4 w-4 text-gray-400" />
                              <span>{lesson}</span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>

          <div className="border-b pb-4 mb-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Sessions & Details</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
              {mockCourse.sessions.map((session, index) => (
                <li key={index} className="flex items-center space-x-2 p-2 rounded-lg bg-gray-50 border border-gray-200">
                  <CalendarDaysIcon className="h-5 w-5 text-indigo-500" />
                  <span className="font-medium">{moment(session.date).format('MMMM D, YYYY')}</span>
                  <span className="text-sm">at {session.time}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Resources</h3>
            <ul className="space-y-2">
              {mockCourse.resources.map((resource, index) => (
                <li key={index} className="flex items-center space-x-2">
                  <LinkIcon className="h-5 w-5 text-indigo-500" />
                  <a href={resource.link} className="text-blue-600 hover:underline">{resource.title}</a>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Sidebar: Instructor and Enrollment */}
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
          className="lg:col-span-1 bg-white p-6 rounded-3xl shadow-lg h-full flex flex-col"
        >
          <div className="flex-1">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Instructor</h3>
            <div className="text-center p-4">
                <UserCircleIcon className="mx-auto h-20 w-20 text-gray-400" />
                <h4 className="mt-4 text-xl font-semibold text-gray-900">{mockCourse.instructor.name}</h4>
                <p className="mt-2 text-sm text-gray-600">{mockCourse.instructor.bio}</p>
            </div>
          </div>
          
          <div className="border-t pt-6 mt-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Enrollment</h3>
            <button
              onClick={handleEnroll}
              disabled={isEnrolled}
              className={`w-full py-3 px-4 rounded-full text-sm font-medium transition-colors shadow-lg
                ${isEnrolled ? 'bg-green-600 text-white cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
            >
              {isEnrolled ? (
                <span className="flex items-center justify-center space-x-2">
                  <UsersIcon className="h-5 w-5" />
                  <span>Enrolled!</span>
                </span>
              ) : (
                <span className="flex items-center justify-center space-x-2">
                  <BookOpenIcon className="h-5 w-5" />
                  <span>Enroll Now</span>
                </span>
              )}
            </button>
            <AnimatePresence>
                {isEnrolled && (
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mt-2 text-center text-sm font-semibold text-green-700"
                    >
                        You have been enrolled in the course.
                    </motion.p>
                )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CourseDetails;